// src/components/modals/PointsStampsConfigModal.jsx
import React, { useState, useEffect } from 'react';
import { FaCog, FaSpinner, FaCheck, FaTimes, FaInfoCircle, FaStamp } from 'react-icons/fa';
import Swal from 'sweetalert2';

const PointsStampsConfigModal = ({ isOpen, onClose, onSubmit, marca }) => {
    const [formData, setFormData] = useState({
        pointsEngineVersion: 1, // Siempre automático cuando se configura
        pointsRules: null
    });

    const [isSubmitting, setIsSubmitting] = useState(false);

    // Configuración por defecto para tarjetas de sellos
    const puntosTicket = marca?.puntosTicket || 12;

    const getDefaultRules = () => ({
        mode: 'RATE_PER_AMOUNT',
        currency: marca?.pointsRules?.currency || 'EUR',
        rounding: 'FLOOR', // Redondear hacia abajo para sellos
        minSpend: 0,
        maxPointsPerTxn: puntosTicket, // Siempre usar puntosTicket (fijo)
        ratePerAmount: {
            rate: 1, // 1 punto (sello) por defecto
            step: 1  // Cada 1 unidad monetaria por defecto
        }
    });

    // Cargar datos existentes
    useEffect(() => {
        if (isOpen && marca) {
            if (marca.pointsEngineVersion === 1 && marca.pointsRules) {
                // Si ya tiene configuración, cargarla
                setFormData({
                    pointsEngineVersion: 1,
                    pointsRules: marca.pointsRules
                });
            } else {
                // Si no tiene configuración, inicializar con reglas por defecto
                setFormData({
                    pointsEngineVersion: 1,
                    pointsRules: getDefaultRules()
                });
            }
        }
    }, [isOpen, marca]);

    const handleRuleChange = (field, value) => {
        setFormData(prev => ({
            ...prev,
            pointsRules: {
                ...prev.pointsRules,
                [field]: value
            }
        }));
    };

    const handleNestedRuleChange = (parent, field, value) => {
        setFormData(prev => ({
            ...prev,
            pointsRules: {
                ...prev.pointsRules,
                [parent]: {
                    ...prev.pointsRules[parent],
                    [field]: value
                }
            }
        }));
    };

    // Función para obtener el símbolo de moneda
    const getCurrencySymbol = (currency) => {
        const symbols = {
            'EUR': '€',
            'USD': '$',
            'PEN': 'S/',
            'CLP': '$'
        };
        return symbols[currency] || '€';
    };

    // Simular cálculo de puntos para preview
    const simulatePoints = (amount) => {
        if (!formData.pointsRules || !formData.pointsRules.ratePerAmount) return 0;

        const { rate, step } = formData.pointsRules.ratePerAmount;
        if (!step || step <= 0) return 0;

        // Calcular puntos según la regla: cada X monto = Y puntos
        const puntos = Math.floor(amount / step) * rate;

        // Aplicar redondeo
        const rounding = formData.pointsRules.rounding || 'FLOOR';
        switch (rounding) {
            case 'FLOOR':
                return Math.floor(puntos);
            case 'CEIL':
                return Math.ceil(puntos);
            case 'ROUND':
                return Math.round(puntos);
            default:
                return Math.floor(puntos);
        }
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        setIsSubmitting(true);

        try {
            // Validar que step sea mayor que 0
            if (!formData.pointsRules.ratePerAmount.step || formData.pointsRules.ratePerAmount.step <= 0) {
                Swal.fire({
                    title: 'Error',
                    text: 'El monto debe ser mayor que 0',
                    icon: 'error',
                    confirmButtonText: 'Aceptar'
                });
                setIsSubmitting(false);
                return;
            }

            // Validar que rate sea mayor que 0
            if (!formData.pointsRules.ratePerAmount.rate || formData.pointsRules.ratePerAmount.rate <= 0) {
                Swal.fire({
                    title: 'Error',
                    text: 'Los puntos deben ser mayor que 0',
                    icon: 'error',
                    confirmButtonText: 'Aceptar'
                });
                setIsSubmitting(false);
                return;
            }

            // Limpiar datos para enviar solo los campos que el backend acepta
            // maxPointsPerTxn siempre usa puntosTicket de la marca (fijo)
            const cleanData = {
                pointsEngineVersion: formData.pointsEngineVersion,
                pointsRules: {
                    mode: formData.pointsRules.mode,
                    currency: formData.pointsRules.currency,
                    rounding: formData.pointsRules.rounding,
                    minSpend: formData.pointsRules.minSpend || 0,
                    maxPointsPerTxn: puntosTicket, // Siempre usar puntosTicket de la marca (fijo)
                    ratePerAmount: {
                        rate: formData.pointsRules.ratePerAmount.rate,
                        step: formData.pointsRules.ratePerAmount.step
                    }
                }
            };

            await onSubmit(cleanData);
            Swal.fire({
                title: 'Configuración Guardada',
                text: 'Motor de puntos automático configurado para tarjetas de sellos. Los puntos se calcularán automáticamente según el monto de compra.',
                icon: 'success',
                confirmButtonText: 'Aceptar'
            });
            onClose();
        } catch (error) {
            console.error('Error saving points rules:', error);
            Swal.fire({
                title: 'Error',
                text: 'No se pudo guardar la configuración',
                icon: 'error',
                confirmButtonText: 'Aceptar'
            });
        } finally {
            setIsSubmitting(false);
        }
    };

    const handleClose = () => {
        setFormData({
            pointsEngineVersion: 1,
            pointsRules: null
        });
        onClose();
    };

    if (!isOpen) return null;

    const currencySymbol = getCurrencySymbol(formData.pointsRules?.currency || 'EUR');

    return (
        <div className="fixed inset-0 z-50 overflow-y-auto">
            <div className="flex items-center justify-center min-h-screen p-2 sm:p-4">
                {/* Fondo oscuro */}
                <div className="fixed inset-0 transition-opacity" aria-hidden="true" onClick={handleClose}>
                    <div className="absolute inset-0 bg-gray-500 opacity-75"></div>
                </div>

                {/* Contenedor del modal */}
                <div className="bg-white rounded-lg sm:rounded-xl text-left overflow-hidden shadow-xl transform transition-all w-full max-w-2xl mx-auto relative z-10 my-4 sm:my-8 max-h-[95vh] flex flex-col">
                    {/* Header - Sticky en móviles */}
                    <div className="px-4 sm:px-6 pt-4 sm:pt-6 pb-3 sm:pb-4 flex-shrink-0 border-b border-gray-200 bg-white">
                        <div className="flex justify-between items-start gap-2">
                            <h3 className="text-base sm:text-lg font-semibold text-gray-900 flex items-center flex-1 min-w-0">
                                <FaStamp className="w-4 h-4 sm:w-5 sm:h-5 mr-2 text-blue-500 flex-shrink-0" />
                                <span className="truncate">Configurar Tarjetas de Sellos</span>
                            </h3>
                            <button
                                onClick={handleClose}
                                className="text-gray-400 hover:text-gray-600 transition-colors p-1 flex-shrink-0"
                                aria-label="Cerrar"
                            >
                                <FaTimes className="w-5 h-5 sm:w-6 sm:h-6" />
                            </button>
                        </div>
                    </div>

                    {/* Contenido scrollable */}
                    <div className="flex-1 overflow-y-auto px-4 sm:px-6 py-4 sm:py-6">

                        {/* Contenido */}
                        <form onSubmit={handleSubmit} className="space-y-4 sm:space-y-6">
                            {/* Información del motor automático */}
                            <div className="bg-blue-50 border border-blue-200 rounded-lg p-3 sm:p-4">
                                <div className="flex items-start">
                                    <FaInfoCircle className="text-blue-500 mt-0.5 sm:mt-1 mr-2 sm:mr-3 flex-shrink-0 w-4 h-4 sm:w-5 sm:h-5" />
                                    <div className="flex-1 min-w-0">
                                        <h4 className="text-xs sm:text-sm font-medium text-blue-800 mb-1">Tarjetas de Sellos Digitales</h4>
                                        <p className="text-xs text-blue-600 mb-2 leading-relaxed">
                                            Configura cómo se acumulan los puntos (sellos) automáticamente según el monto de compra.
                                            Cada punto equivale a un sello completado en la tarjeta.
                                        </p>
                                        <p className="text-xs text-blue-600">
                                            <strong>Tarjeta configurada:</strong> {puntosTicket} sellos necesarios para completar la tarjeta.
                                        </p>
                                    </div>
                                </div>
                            </div>

                            {/* Configuración del motor automático */}
                            {formData.pointsRules && (
                                <div className="space-y-4 sm:space-y-6">
                                    {/* Configuración principal: Cada X monto = Y puntos */}
                                    <div className="bg-white border border-gray-200 rounded-lg p-3 sm:p-4">
                                        <h4 className="text-xs sm:text-sm font-medium text-gray-700 mb-3 sm:mb-4">
                                            💰 Regla de Acumulación de Sellos
                                        </h4>

                                        {/* Layout responsive: vertical en móvil, horizontal en desktop */}
                                        <div className="space-y-4 sm:space-y-0 sm:grid sm:grid-cols-3 sm:gap-4 sm:items-end">
                                            <div className="sm:col-span-1">
                                                <label className="block text-xs sm:text-sm font-medium text-gray-700 mb-2">
                                                    Cada cuánto monto
                                                </label>
                                                <div className="relative">
                                                    <input
                                                        type="number"
                                                        min="0.01"
                                                        step="0.01"
                                                        value={formData.pointsRules.ratePerAmount?.step || 1}
                                                        onChange={(e) => handleNestedRuleChange('ratePerAmount', 'step', Number(e.target.value))}
                                                        className="w-full pl-8 sm:pl-10 pr-3 py-2.5 sm:py-2 text-sm sm:text-base border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                                                        placeholder="100"
                                                        required
                                                    />
                                                    <span className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-500 text-sm sm:text-base pointer-events-none">
                                                        {currencySymbol}
                                                    </span>
                                                </div>
                                                <p className="text-xs text-gray-500 mt-1">
                                                    Ejemplo: 100 = cada 100{currencySymbol}
                                                </p>
                                            </div>

                                            <div className="sm:col-span-1 flex items-center justify-center py-2 sm:py-0">
                                                <span className="text-gray-600 font-medium text-xl sm:text-2xl">=</span>
                                            </div>

                                            <div className="sm:col-span-1">
                                                <label className="block text-xs sm:text-sm font-medium text-gray-700 mb-2">
                                                    Puntos (sellos)
                                                </label>
                                                <input
                                                    type="number"
                                                    min="1"
                                                    step="1"
                                                    value={formData.pointsRules.ratePerAmount?.rate || 1}
                                                    onChange={(e) => handleNestedRuleChange('ratePerAmount', 'rate', Number(e.target.value))}
                                                    className="w-full px-3 py-2.5 sm:py-2 text-sm sm:text-base border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                                                    placeholder="1"
                                                    required
                                                />
                                                <p className="text-xs text-gray-500 mt-1">
                                                    Ejemplo: 1 = 1 sello, 2 = 2 sellos
                                                </p>
                                            </div>
                                        </div>

                                        {/* Ejemplo visual */}
                                        <div className="mt-4 p-3 sm:p-4 bg-gray-50 border border-gray-200 rounded-lg">
                                            <p className="text-xs sm:text-sm text-gray-600 mb-2 sm:mb-3">
                                                <strong>Ejemplo:</strong> Con esta configuración:
                                            </p>
                                            <div className="text-xs sm:text-sm text-gray-700 space-y-1.5 sm:space-y-2">
                                                <p className="break-words">
                                                    • Compra de {formData.pointsRules.ratePerAmount?.step || 1}{currencySymbol} = <strong className="text-gray-900">{simulatePoints(formData.pointsRules.ratePerAmount?.step || 1)} sello(s)</strong>
                                                </p>
                                                <p className="break-words">
                                                    • Compra de {(formData.pointsRules.ratePerAmount?.step || 1) * 2}{currencySymbol} = <strong className="text-gray-900">{simulatePoints((formData.pointsRules.ratePerAmount?.step || 1) * 2)} sello(s)</strong>
                                                </p>
                                                <p className="break-words">
                                                    • Compra de {(formData.pointsRules.ratePerAmount?.step || 1) * 5}{currencySymbol} = <strong className="text-gray-900">{simulatePoints((formData.pointsRules.ratePerAmount?.step || 1) * 5)} sello(s)</strong>
                                                </p>
                                            </div>
                                        </div>
                                    </div>

                                    {/* Configuraciones adicionales */}
                                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                                        <div>
                                            <label className="block text-xs sm:text-sm font-medium text-gray-700 mb-2">
                                                💱 Moneda de Referencia
                                            </label>
                                            <select
                                                value={formData.pointsRules.currency || 'EUR'}
                                                onChange={(e) => handleRuleChange('currency', e.target.value)}
                                                className="w-full px-3 py-2.5 sm:py-2 text-sm sm:text-base border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                                            >
                                                <option value="EUR">🇪🇺 Euro (€)</option>
                                                <option value="USD">🇺🇸 Dólar Americano ($)</option>
                                                <option value="PEN">🇵🇪 Sol Peruano (S/)</option>
                                                <option value="CLP">🇨🇱 Peso Chileno ($)</option>
                                            </select>
                                            <p className="text-xs text-gray-500 mt-1">
                                                Moneda en la que se basarán los cálculos
                                            </p>
                                        </div>
                                        <div>
                                            <label className="block text-xs sm:text-sm font-medium text-gray-700 mb-2">
                                                🔢 Método de Redondeo
                                            </label>
                                            <select
                                                value={formData.pointsRules.rounding || 'FLOOR'}
                                                onChange={(e) => handleRuleChange('rounding', e.target.value)}
                                                className="w-full px-3 py-2.5 sm:py-2 text-sm sm:text-base border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                                            >
                                                <option value="FLOOR">Redondear hacia abajo (recomendado)</option>
                                                <option value="CEIL">Redondear hacia arriba</option>
                                                <option value="ROUND">Redondear normal</option>
                                            </select>
                                            <p className="text-xs text-gray-500 mt-1">
                                                Cómo manejar los decimales en el cálculo
                                            </p>
                                        </div>
                                    </div>

                                    {/* Límites */}
                                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                                        <div>
                                            <label className="block text-xs sm:text-sm font-medium text-gray-700 mb-2">
                                                💰 Gasto Mínimo Requerido
                                            </label>
                                            <input
                                                type="number"
                                                min="0"
                                                step="0.01"
                                                value={formData.pointsRules.minSpend || 0}
                                                onChange={(e) => handleRuleChange('minSpend', Number(e.target.value))}
                                                className="w-full px-3 py-2.5 sm:py-2 text-sm sm:text-base border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                                                placeholder="0"
                                            />
                                            <p className="text-xs text-gray-500 mt-1">
                                                Monto mínimo para obtener sellos (0 = sin mínimo)
                                            </p>
                                        </div>
                                        <div>
                                            <label className="block text-xs sm:text-sm font-medium text-gray-700 mb-2">
                                                🚫 Límite Máximo de Sellos por Compra
                                            </label>
                                            <div className="w-full px-3 py-2.5 sm:py-2 text-sm sm:text-base border border-gray-200 bg-gray-50 rounded-lg text-gray-700 font-medium">
                                                {puntosTicket} sellos (fijo según tarjeta)
                                            </div>
                                            <p className="text-xs text-gray-500 mt-1">
                                                Este límite está fijado según el tamaño de tu tarjeta ({puntosTicket} sellos) y no se puede modificar
                                            </p>
                                        </div>
                                    </div>

                                    {/* Preview de cálculo */}
                                    <div className="bg-green-50 border border-green-200 rounded-lg p-3 sm:p-4">
                                        <h4 className="text-xs sm:text-sm font-medium text-green-800 mb-2 flex items-center">
                                            <FaInfoCircle className="mr-1.5 w-3 h-3 sm:w-4 sm:h-4" />
                                            📊 Vista Previa de Cálculos
                                        </h4>
                                        <p className="text-xs text-green-600 mb-3 leading-relaxed">
                                            Ejemplos de cuántos sellos obtendrían tus clientes con diferentes montos de compra:
                                        </p>
                                        <div className="grid grid-cols-1 sm:grid-cols-3 gap-3 sm:gap-4">
                                            <div className="bg-white border border-green-200 rounded-lg p-3 sm:p-4">
                                                <div className="text-center">
                                                    <span className="text-xs sm:text-sm text-green-600 font-medium block">Compra de 25{currencySymbol}</span>
                                                    <div className="text-base sm:text-lg font-bold text-green-800 mt-1.5 sm:mt-2">
                                                        {simulatePoints(25)} sello(s)
                                                    </div>
                                                </div>
                                            </div>
                                            <div className="bg-white border border-green-200 rounded-lg p-3 sm:p-4">
                                                <div className="text-center">
                                                    <span className="text-xs sm:text-sm text-green-600 font-medium block">Compra de 50{currencySymbol}</span>
                                                    <div className="text-base sm:text-lg font-bold text-green-800 mt-1.5 sm:mt-2">
                                                        {simulatePoints(50)} sello(s)
                                                    </div>
                                                </div>
                                            </div>
                                            <div className="bg-white border border-green-200 rounded-lg p-3 sm:p-4">
                                                <div className="text-center">
                                                    <span className="text-xs sm:text-sm text-green-600 font-medium block">Compra de 100{currencySymbol}</span>
                                                    <div className="text-base sm:text-lg font-bold text-green-800 mt-1.5 sm:mt-2">
                                                        {simulatePoints(100)} sello(s)
                                                    </div>
                                                </div>
                                            </div>
                                        </div>
                                    </div>

                                    {/* Nota sobre funcionalidad futura */}
                                    <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-3 sm:p-4">
                                        <p className="text-xs text-yellow-700 leading-relaxed">
                                            <strong>💡 Próximamente:</strong> Podrás configurar reglas específicas por producto (ej: 1 café = 1 sello).
                                            Por ahora, la acumulación se basa únicamente en el monto de compra.
                                        </p>
                                    </div>
                                </div>
                            )}

                            {/* Botones - Footer sticky en móviles */}
                            <div className="flex flex-col sm:flex-row justify-end gap-2 sm:gap-3 pt-4 sm:pt-6 border-t border-gray-200 mt-4 sm:mt-6">
                                <button
                                    type="button"
                                    onClick={handleClose}
                                    className="w-full sm:w-auto px-4 py-2.5 sm:py-2 text-sm sm:text-base text-gray-700 bg-gray-100 border border-gray-300 rounded-lg hover:bg-gray-200 transition-colors font-medium"
                                >
                                    Cancelar
                                </button>
                                <button
                                    type="submit"
                                    disabled={isSubmitting}
                                    className="w-full sm:w-auto px-4 py-2.5 sm:py-2 text-sm sm:text-base bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:bg-blue-400 disabled:cursor-not-allowed transition-colors font-medium flex items-center justify-center"
                                >
                                    {isSubmitting ? (
                                        <>
                                            <FaSpinner className="w-4 h-4 mr-2 animate-spin" />
                                            Guardando...
                                        </>
                                    ) : (
                                        <>
                                            <FaCheck className="w-4 h-4 mr-2" />
                                            Guardar Configuración
                                        </>
                                    )}
                                </button>
                            </div>
                        </form>
                    </div>
                </div>
            </div>
        </div>
    );
};

export default PointsStampsConfigModal;

